import React from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import Alert from '../alert/alert'
import SearchField from '../logs/logSearchField'
import { get } from '../../utils/service'
import { Log } from '../../types/log'
import { PagedResponse } from '../../types/pagedResponse'
import { LegacyDatapoint, Datapoint } from '../../types/datapoint'
import { LogEvent } from '../../types/logEvent'
import { GraphSeriesConfig, GraphSeriesEvents } from '../../types/graphSeriesConfig'
import {
  getLogName,
  getLogsUrl,
  getEventsFromLogOrDatapoints,
  legacyDatapointsToDatapoints,
} from '../../utils/logUtils'
import { datapointsToSeries, getCompareSeriesConfig, getLogEvents, getPlotSeriesFromLog } from '../../utils/graphUtils'
import { Machine } from '../../types/machine'
import { BasicDataParam, GraphDataParam } from '../../types/enums/graphDataParam'
import { Sensorconfig } from '../../types/sensorconfig'
import { getCompareLiveParams, setCompareLiveParams } from '../../utils/localStorageUtils'
import { User } from '../../types/user'
import Pagination from '../fields/pagination'
import { toReadableTimestamp } from '../../utils/dateUtils'
import CompareCheckboxGroup from '../compareLogs/compareCheckboxGroup'

interface LiveCompareLogsProps {
  currentMachine: Machine
  user: User | null
  sensorconfig: Sensorconfig | null
  onSetCompareSeriesConfig: (compareSeriesConfig: Array<GraphSeriesConfig>) => void
  onSetCompareEventsConfig: (compareEventsConfig: GraphSeriesEvents | null) => void
}

const LiveCompareLogs = (props: LiveCompareLogsProps): JSX.Element => {
  const { currentMachine, sensorconfig, onSetCompareSeriesConfig, onSetCompareEventsConfig } = props
  const { t } = useTranslation()
  const theme = useTheme()

  const [isAccordionExpanded, setIsAccordionExpanded] = React.useState<boolean>(false)
  const [compareLogs, setCompareLogs] = React.useState<Array<Log> | null>(null)
  const [isLoadingCompareLogs, setIsLoadingCompareLogs] = React.useState<boolean>(false)
  const [hasCompareLogsError, setHasCompareLogsError] = React.useState<boolean>(false)
  const [loadCompareLogsUrl, setLoadCompareLogsUrl] = React.useState<string>('')
  const [paginationSettings, setPaginationSettings] = React.useState<PagedResponse<Log> | null>(null)
  const [compareLogLoadingId, setCompareLogLoadingId] = React.useState<number>(0)
  const [compareLogErrorId, setCompareLogErrorId] = React.useState<number>(0)

  const [compareDatapoints, setCompareDatapoints] = React.useState<Array<Datapoint> | null>(null)
  const [compareEvents, setCompareEvents] = React.useState<Array<LogEvent> | null>(null)
  const [compareLog, setCompareLog] = React.useState<Log | null>(null)
  const [compareParams, setCompareParams] = React.useState<Array<GraphDataParam>>(getCompareLiveParams())

  // when machineId is changed, clear all data
  React.useEffect(() => {
    setCompareLogs(null)
    setCompareDatapoints(null)
    setCompareEvents(null)
    setIsAccordionExpanded(false)
  }, [currentMachine.id])

  // when compareSeries are changed, run props callback to draw series
  React.useEffect(() => {
    const compareSeriesConfig: Array<GraphSeriesConfig> = []
    if (compareLog && compareDatapoints) {
      const availableSeries = getPlotSeriesFromLog(compareDatapoints, sensorconfig, theme, props.user)
      const seriesConfig = availableSeries.filter((x) => compareParams.indexOf(x.param) > -1)
      seriesConfig.forEach((series: GraphSeriesConfig) => {
        const seriesLabel = `${getLogName(compareLog, '', t('logview.batch'))} ${t(series.seriesLabel)}`
        compareSeriesConfig.push(
          getCompareSeriesConfig(
            series.param,
            compareLog,
            seriesLabel,
            series.color,
            series.seriesType,
            compareDatapoints,
            props.user
          )
        )
      })
    }
    onSetCompareSeriesConfig(compareSeriesConfig)
  }, [compareParams, compareLog, sensorconfig, compareDatapoints, props.user, theme, onSetCompareSeriesConfig, t])

  // when compareLog is changed, run props callback to draw events
  React.useEffect(() => {
    const compareBtPoints = datapointsToSeries(compareDatapoints || [], BasicDataParam.BT)
    const eventData =
      compareLog && compareEvents ? getLogEvents(compareEvents, compareLog, t('logview.batch'), compareBtPoints) : null
    onSetCompareEventsConfig(eventData)
  }, [compareLog, compareEvents, compareDatapoints, onSetCompareEventsConfig, t])

  React.useEffect(() => {
    if (loadCompareLogsUrl) {
      setCompareLogs(null)
      setIsLoadingCompareLogs(true)
      setHasCompareLogsError(false)
      get<PagedResponse<Log>>(loadCompareLogsUrl)
        .then((response) => {
          setCompareLogs(response.data.results)
          setPaginationSettings(response.data)
          setIsLoadingCompareLogs(false)
        })
        .catch(() => {
          setIsLoadingCompareLogs(false)
          setHasCompareLogsError(true)
        })
    }
    // reload logs when new batch number is set (to add previous log to list)
  }, [loadCompareLogsUrl, props.currentMachine.next_batch_no])

  // when compare params are changed; update localStorage
  React.useEffect(() => {
    setCompareLiveParams(compareParams)
  }, [compareParams])

  const onChangeCompareLog = (logData: Log): void => {
    if (logData.id === compareLog?.id) {
      setCompareLog(null)
    } else {
      setCompareLogErrorId(0)
      setCompareLogLoadingId(logData.id)
      setCompareDatapoints(null)
      setCompareEvents(null)
      get<Array<LegacyDatapoint>>(`datapoints/?page_size=all&log=${logData.id}`)
        .then((result) => {
          setCompareLogLoadingId(0)
          setCompareDatapoints(legacyDatapointsToDatapoints(result.data))
          setCompareEvents(getEventsFromLogOrDatapoints(logData, result.data))
        })
        .catch(() => {
          setCompareLogLoadingId(0)
          setCompareLogErrorId(logData.id)
        })
      setCompareLog(logData)
    }
  }

  const calculateAvailableCompareSeries = (): Array<GraphSeriesConfig> => {
    return compareDatapoints
      ? getPlotSeriesFromLog(compareDatapoints, sensorconfig, theme, props.user).filter(
          (x) => x.param !== BasicDataParam.CRACK
        )
      : []
  }

  return (
    <Accordion
      disableGutters
      expanded={isAccordionExpanded}
      sx={{
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        transition: 'background-color 0.3s',
        '&:hover': { backgroundColor: 'primary.dark' },
        '&.Mui-expanded': {
          backgroundColor: 'primary.lighter',
          color: 'secondary.contrastText',
        },
      }}
      onChange={(_event, expanded: boolean) => {
        if (expanded && !compareLogs) {
          setLoadCompareLogsUrl(getLogsUrl(currentMachine.id))
        }
        setIsAccordionExpanded(expanded)
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              color: 'primary.contrastText',
            }}
          />
        }
        sx={{
          '&.Mui-expanded': {
            '& .MuiSvgIcon-root': {
              color: 'secondary.contrastText',
            },
          },
        }}
      >
        <Typography variant="button">{t('logview.comparelogs')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <SearchField
            placeholder={t('logsview.searchlogs')}
            onSearch={(searchString: string): void => {
              const newLoadUrl = getLogsUrl(currentMachine.id, searchString)
              if (newLoadUrl !== loadCompareLogsUrl) {
                setPaginationSettings(null)
                setLoadCompareLogsUrl(newLoadUrl)
              }
            }}
          />

          {/* This is mapping over all batches */}
          {(compareLogs || []).map((summary) => {
            const bean_name = summary.inventory_name || summary.bean_name
            return (
              <div>
                <Accordion
                  key={summary.id}
                  expanded={summary.id === compareLog?.id}
                  disableGutters
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    flexDirection: 'column',
                    backgroundColor: 'background.default',
                    borderRadius: '4px',
                    color: 'secondary.contrastText',
                    transition: 'background-color 0.3s',
                    '&:hover': { backgroundColor: 'primary.lighter', border: 1, borderColor: 'primary.main' },
                    '&.Mui-expanded': {
                      backgroundColor: 'background.default',
                      border: 1,
                      borderColor: 'primary.main',
                      color: 'secondary.contrastText',
                    },
                  }}
                  TransitionProps={{ unmountOnExit: true }}
                  onChange={(_event, expanded: boolean) => {
                    onChangeCompareLog(summary)
                  }}
                >
                  <AccordionSummary>
                    <Stack sx={{ justifyContent: 'flex-start' }}>
                      <Typography variant="button">{getLogName(summary, '', t('logview.batch'))}</Typography>
                      <Typography variant="caption">{toReadableTimestamp(summary.start_timestamp)}</Typography>
                      <Typography variant="body2" sx={{ marginTop: 1 }}>
                        {t('logsview.profilename')}: &nbsp;
                        {summary.profile_name || summary.profile_data?.name || t('logsview.unknownprofile')}
                      </Typography>
                      {bean_name && (
                        <Typography variant="body2">
                          {t('logsview.beanname')}: &nbsp;
                          {bean_name}
                        </Typography>
                      )}
                    </Stack>
                  </AccordionSummary>

                  {/* Compare Params menu */}
                  <AccordionDetails>
                    {summary.id === compareLogErrorId && (
                      <Alert severity="error">{t('logview.comparedatapointserror')}</Alert>
                    )}
                    {summary.id === compareLogLoadingId && <CircularProgress />}
                    {summary.id === compareLog?.id && (
                      <CompareCheckboxGroup
                        seriesConfig={calculateAvailableCompareSeries()}
                        checkedCompareParams={compareParams}
                        onChange={(param: GraphDataParam): void => {
                          setCompareParams((oldSeries) => {
                            return oldSeries.find((y) => y === param)
                              ? oldSeries.filter((y) => y !== param)
                              : [...oldSeries, param]
                          })
                        }}
                        row={false}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              </div>
            )
          })}
          {isLoadingCompareLogs && <CircularProgress />}
          {paginationSettings && (
            <Pagination
              isLoading={isLoadingCompareLogs}
              paginationSettings={paginationSettings}
              onChangePage={(newPageUrl: string) => setLoadCompareLogsUrl(newPageUrl)}
            />
          )}
          {hasCompareLogsError && <Alert severity="error">{t('logview.loadcomparelogerror')}</Alert>}
          {compareLogs && compareLogs.length === 0 && <Alert severity="info">{t('logsview.nologs')}</Alert>}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export default LiveCompareLogs
